import { Box, Breadcrumbs, Skeleton, Typography } from "@mui/material";
import * as QueryKeys from "data";
import {
  AdminAccountDetails,
  Connector,
  DataCatalogueEntry,
  MemberAccountDetails,
  Pipeline,
} from "data/models";
import {
  fetchAdminConnectorById,
  fetchAdminPipelineById,
  fetchAdminUserDetails,
  fetchCatalogueEntry,
  fetchMemberUserDetails,
  fetchOrganisationDetailsAdmin,
} from "data/queries";
import _ from "lodash";
import { AdminOrganisationDetail } from "pages/geoscape-admin/organisation/organisation-details/utils";
import { useQuery } from "react-query";
import { Link, useHistory, useLocation } from "react-router-dom";
import * as Routes from "routes";

// Hook for fetching organization data
const useOrganisationData = (organisationId: string) => {
  return useQuery(
    [QueryKeys.adminOrganisationDetailsQuery, organisationId],
    () => fetchOrganisationDetailsAdmin(organisationId),
    {
      enabled: organisationId !== "",
    }
  );
};

// Hook for fetching user data
const useAdminUserData = (accountId: string, isAdminPage: boolean) => {
  return useQuery(
    [QueryKeys.adminAccountDetailsQuery, accountId],
    () => fetchAdminUserDetails(accountId),
    {
      enabled: accountId !== "" && isAdminPage,
    }
  );
};

const useOrgMembershipData = (accountId: string, isAdminPage: boolean) => {
  return useQuery(
    [QueryKeys.organisationMemberAccount, accountId],
    () => fetchMemberUserDetails(accountId),
    {
      enabled: !!accountId && !isAdminPage,
    }
  );
};

// Hook for fetching connector data
const useConnectorData = (organisationId: string, connectorId: string) => {
  return useQuery(
    [QueryKeys.adminConnector, connectorId],
    () => fetchAdminConnectorById(organisationId, connectorId ?? ""),
    {
      enabled: connectorId !== "" && organisationId !== "",
    }
  );
};

// Hook for fetching pipeline data
const usePipelineData = (organisationId: string, pipelineId: string) => {
  return useQuery(
    [QueryKeys.adminPipeline, pipelineId],
    () => fetchAdminPipelineById(organisationId, pipelineId ?? ""),
    {
      enabled: pipelineId !== "" && organisationId !== "",
    }
  );
};

// Hook for fetching catalogue entry data
const useCatalogueEntryData = (productName: string) => {
  return useQuery(
    [QueryKeys.catalogueEntryKey, productName],
    () => fetchCatalogueEntry(productName),
    {
      enabled: productName !== "",
    }
  );
};

// Breadcrumb segment name mappings
const BREADCRUMB_NAMES: Record<string, string> = {
  "national-buildings-chatswood": "",
  "flood-data-demo": "",
  explorer: "",
  clip: "",
  "api-keys": "Apps",
  admin: "Geoscape Admin",
  demos: "Demos & Prototypes",
  management: "Customer Management",
  security: "User Security",
  predictive:
    "Address Auto-complete Demo (Predictive API + Addresses API + Maps API)",
};

// Route redirect mappings
const ROUTE_REDIRECTS: Record<string, string> = {
  "/admin": "/admin/dashboard",
  "/admin/customer-management": "/admin/customer-management/accounts",
};

// Clean up release names (e.g., "Patch-Postcode-Fix-2024-2" -> "Patch Postcode Fix")
const formatReleaseName = (name: string): string => {
  return name
    .replace(/-\d{4}-\d+$/, "") // Remove year pattern
    .replace(/-/g, " "); // Convert dashes to spaces
};

// Format breadcrumb segment based on context
const formatBreadcrumbSegment = (
  segment: string,
  pathContext: string,
  segmentIndex: number,
  pathArray: string[],
  orgData?: AdminOrganisationDetail,
  userData?: AdminAccountDetails,
  connectorData?: Connector,
  pipelineData?: Pipeline,
  memberData?: MemberAccountDetails,
  catalogueData?: DataCatalogueEntry
): string => {
  // Handle catalogue routes - use displayProduct instead of ID
  if (pathContext.includes("/catalogue/") && catalogueData?.id === segment) {
    return catalogueData.displayProduct;
  }

  // Handle admin organization routes - replace org ID with org name
  if (
    pathContext.includes("/admin/organisations/") &&
    orgData?.organisationId === segment
  ) {
    return orgData.name;
  }

  // On org management user role page we also have a user id to swap in
  if (
    pathContext.includes("/admin/organisations/") &&
    userData?.username === segment
  ) {
    return `${userData.firstName} ${userData.lastName}`;
  }

  if (
    pathContext.includes("/admin/organisations/") &&
    pathContext.includes("/connectors/") &&
    connectorData?.connectorId === segment
  ) {
    return `${connectorData.name}`;
  }

  if (
    pathContext.includes("/admin/organisations/") &&
    pathContext.includes("/pipelines/") &&
    pipelineData?.pipelineId === segment
  ) {
    return `${pipelineData.name}`;
  }

  if (
    pathContext.includes("/settings/organisation/") &&
    memberData?.username === segment
  ) {
    return `${memberData.firstName} ${memberData.lastName}`;
  }

  // Handle admin user routes - replace user ID with username
  if (pathContext.includes("/admin/users/") && userData?.username === segment) {
    return `${userData.firstName} ${userData.lastName}`;
  }

  // Handle release management edit routes - format release names (only for the release name segment)
  if (
    pathContext.includes("/admin/release-management/") &&
    pathArray[segmentIndex - 1] === "release-management"
  ) {
    return formatReleaseName(segment);
  }

  // Handle breadcrumb name mappings
  if (BREADCRUMB_NAMES[segment] !== undefined) {
    return BREADCRUMB_NAMES[segment];
  }

  // Default to start case
  return _.startCase(segment);
};

// Generate link path with redirects applied
const generateLinkPath = (
  basePath: string,
  segment: string,
  segmentIndex: number,
  pathArray: string[]
): string => {
  let linkPath = basePath;

  // Apply route redirects
  if (ROUTE_REDIRECTS[linkPath]) {
    linkPath = ROUTE_REDIRECTS[linkPath];
  }

  // Special case: organization routes need /general suffix
  if (pathArray[segmentIndex - 1] === "organisations" && segmentIndex === 2) {
    linkPath += "/general";
  }

  return linkPath;
};

const NO_CRUMBS = [Routes.hub, Routes.whatsNew];

export const DynamicBreadcrumbsMenu = () => {
  const history = useHistory();
  const location = useLocation();
  const pathArray = location.pathname.substring(1).split("/");
  const isAdminPage = pathArray.includes("admin");

  // Extract IDs from URL
  const organisationId = location.pathname.includes(
    `${Routes.adminOrganisations}/`
  )
    ? pathArray[2]
    : "";

  // A user ID might be in position [2] on a admin-user route, or in position [4] on an admin org route
  const getAccountId = () => {
    if (location.pathname.includes(`${Routes.adminUsers}/`)) {
      return pathArray[2];
    }

    if (
      location.pathname.includes(`${Routes.adminOrganisations}/`) &&
      pathArray[4] &&
      pathArray[3] == "members"
    ) {
      return pathArray[4];
    }

    if (location.pathname.includes(`/settings/organisation/`) && pathArray[4]) {
      return pathArray[4];
    }
    return "";
  };

  const accountId = getAccountId();
  const connectorId =
    location.pathname.includes(`${Routes.adminOrganisations}/`) &&
    location.pathname.includes(`/connectors/`)
      ? pathArray[5]
      : "";

  const pipelineId =
    location.pathname.includes(`${Routes.adminOrganisations}/`) &&
    !location.pathname.endsWith("create") &&
    location.pathname.includes(`/pipelines/`)
      ? pathArray[4]
      : "";

  const catalogueProductName = location.pathname.includes("/catalogue/")
    ? pathArray[1]
    : "";

  // Fetch data
  const orgQuery = useOrganisationData(organisationId);
  const userQuery = useAdminUserData(accountId, isAdminPage);
  const connectorQuery = useConnectorData(organisationId, connectorId);
  const pipelineQuery = usePipelineData(organisationId, pipelineId);
  const memberQuery = useOrgMembershipData(accountId, isAdminPage);
  const catalogueQuery = useCatalogueEntryData(catalogueProductName);

  const isLoading =
    orgQuery.isLoading ||
    userQuery.isLoading ||
    connectorQuery.isLoading ||
    catalogueQuery.isLoading;

  const showBreadcrumbs =
    isAdminPage ||
    pathArray.includes("property-insights") ||
    pathArray.includes("whats-new");

  // Get page title
  const getPageTitle = (): string => {
    const lastSegment = pathArray[pathArray.length - 1];
    const lastIndex = pathArray.length - 1;

    return formatBreadcrumbSegment(
      lastSegment,
      location.pathname,
      lastIndex,
      pathArray,
      orgQuery.data,
      userQuery.data,
      connectorQuery.data,
      pipelineQuery.data,
      memberQuery.data,
      catalogueQuery.data
    );
  };

  const noBreadCrumb = NO_CRUMBS.includes(location.pathname);

  if (noBreadCrumb) {
    return <></>;
  }

  return (
    <Box sx={{ zIndex: "1000" }}>
      {!isLoading && <Typography variant="h6">{getPageTitle()}</Typography>}
      {showBreadcrumbs && (
        <>
          {isLoading && (
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              {pathArray.map((_, index) => (
                <Skeleton
                  key={`skeleton-${index}`}
                  variant="rectangular"
                  width="100px"
                  height="18px"
                />
              ))}
            </Breadcrumbs>
          )}

          {!isLoading && (
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              {pathArray.map((segment, index) => {
                const isLastSegment = index === pathArray.length - 1;
                const basePath = "/" + pathArray.slice(0, index + 1).join("/");

                // Format the display name
                const displayName = formatBreadcrumbSegment(
                  segment,
                  location.pathname,
                  index,
                  pathArray,
                  orgQuery.data,
                  userQuery.data,
                  connectorQuery.data,
                  pipelineQuery.data,
                  memberQuery.data,
                  catalogueQuery.data
                );

                // Last segment is not clickable
                if (isLastSegment) {
                  return <Box key={index}>{displayName}</Box>;
                }

                // Admin root is not clickable
                if (basePath === "/admin") {
                  return <Typography key={index}>{displayName}</Typography>;
                }

                // Generate link path with redirects
                const linkPath = generateLinkPath(
                  basePath,
                  segment,
                  index,
                  pathArray
                );

                return (
                  <Link
                    key={index}
                    to={{
                      pathname: linkPath,
                      state: history.location.state as Object,
                    }}
                    style={{ color: "inherit" }}
                  >
                    {displayName}
                  </Link>
                );
              })}
            </Breadcrumbs>
          )}
        </>
      )}
    </Box>
  );
};
